// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../../store';
import { useMsal } from '../../../../recruiter/useMsal';
import { Sidebar } from '../../../../components/Sidebar';
import PageLoader from '../../../../components/PageLoader';
import {
  fetchInterviewReport,
  candidateAction,
  fetchSkillAssessmentReport
} from '../../../../store/interviewHostSlice';
import ActionModal from './ActionModal';
import styles from './InterviewReport.module.scss';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MicIcon from '@mui/icons-material/Mic';
import AssignmentIcon from '@mui/icons-material/Assignment';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { getScoreColor, getRecommendationColor } from '../../../../utils/scoringUtils';
import { generateInterviewReportPDF, generateSkillAssessmentReportPDF } from '../../../../utils/pdfGenerator';

type ReportTab = 'interview' | 'skillAssessment';

const InterviewReport: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { instance } = useMsal();
  const user = useSelector((state: RootState) => state.auth.user);
  const { interviewReport, reportLoading, reportError } = useSelector((state: RootState) => state.interviewHost);
  const { skillAssessmentReport, skillAssessmentReportLoading, skillAssessmentReportError } = useSelector(
    (state: RootState) => state.interviewHost
  );

  const [actionModal, setActionModal] = useState<{
    open: boolean;
    action: 'hold' | 'reject' | 'advance' | null;
  }>({
    open: false,
    action: null
  });
  const [activeTab, setActiveTab] = useState<ReportTab>('interview');
  const [transcriptExpanded, setTranscriptExpanded] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentInterviewReport = interviewReport?.candidate.candidate_id === candidateId ? interviewReport : null;
  const currentSkillAssessmentReport =
    skillAssessmentReport?.header.candidate_id === candidateId ? skillAssessmentReport : null;

  // Fetch interview report first to get candidate configuration
  useEffect(() => {
    if (candidateId) {
      dispatch(fetchInterviewReport({ candidateId }));
    }
  }, [candidateId, dispatch]);

  // Fetch skill assessment report based on configuration
  useEffect(() => {
    if (!candidateId) return;

    // Wait for interview report to finish loading (success or failure)
    if (reportLoading) return;

    const candidate = currentInterviewReport?.candidate;

    if (currentInterviewReport) {
      // Interview report succeeded - check if skill assessment is configured
      const deliveryChannels = candidate?.interview_configuration?.delivery_channels || [];
      const skillAssessmentInChannels = deliveryChannels.includes('ai_skill_assessment');
      const skillAssessmentState = candidate?.skill_assessment?.state;
      const skillAssessmentConfigured =
        skillAssessmentInChannels || (skillAssessmentState && skillAssessmentState !== 'not_started');

      // Fetch if skill assessment was configured
      if (skillAssessmentConfigured) {
        dispatch(fetchSkillAssessmentReport({ candidateId }));
      }
    } else if (reportError) {
      // Interview report failed (404) - try skill assessment anyway
      // (it might be the only interview type configured)
      dispatch(fetchSkillAssessmentReport({ candidateId }));
    }
  }, [candidateId, currentInterviewReport, reportLoading, reportError, dispatch]);

  // Set initial active tab based on which interviews were configured
  // If only skill assessment was sent, default to that tab
  useEffect(() => {
    if (!currentInterviewReport && !currentSkillAssessmentReport) return;

    const candidate = currentInterviewReport?.candidate || currentSkillAssessmentReport?.header;
    if (!candidate) return;

    const deliveryChannels = currentInterviewReport?.candidate?.interview_configuration?.delivery_channels || [];
    const interviewConfigured = deliveryChannels.includes('interview_host') || Boolean(currentInterviewReport);
    const skillConfigured =
      deliveryChannels.includes('ai_skill_assessment') ||
      Boolean(currentSkillAssessmentReport) ||
      currentInterviewReport?.candidate?.skill_assessment?.state !== 'not_started';

    // Default to skill assessment if only skill assessment was configured
    if (!interviewConfigured && skillConfigured && activeTab === 'interview') {
      setActiveTab('skillAssessment');
    }
  }, [currentInterviewReport, currentSkillAssessmentReport, activeTab]);

  // Guard against being stuck on the Skill Assessment tab if it turns out
  // to be unavailable (e.g. the fetch fails after the tab was selected).
  useEffect(() => {
    const interviewUnavailable = !reportLoading && (Boolean(reportError) || !currentInterviewReport);
    const skillUnavailable =
      !skillAssessmentReportLoading && (Boolean(skillAssessmentReportError) || !currentSkillAssessmentReport);
    if (activeTab === 'interview' && interviewUnavailable && !skillUnavailable) {
      setActiveTab('skillAssessment');
    } else if (activeTab === 'skillAssessment' && skillUnavailable && !interviewUnavailable) {
      setActiveTab('interview');
    }
  }, [
    activeTab,
    reportLoading,
    reportError,
    currentInterviewReport,
    skillAssessmentReportLoading,
    skillAssessmentReportError,
    currentSkillAssessmentReport
  ]);

  const handleLogout = useCallback(async () => {
    try {
      // logoutPopup already performs the full-page redirect to /api/sso/logout;
      // navigating again here would cancel that request before the server can
      // destroy the session and clear the cookie.
      await instance.logoutPopup({
        postLogoutRedirectUri: '/login'
      });
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [instance]);

  const handleBackToHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleBackToJobs = useCallback(() => {
    navigate('/jobs');
  }, [navigate]);

  const handleBackToCandidates = useCallback(() => {
    if (currentInterviewReport?.job.job_id) {
      navigate(`/jobs/${currentInterviewReport.job.job_id}/candidates`);
      return;
    }
    const jobHref = currentSkillAssessmentReport?.header.breadcrumbs.find((crumb) =>
      crumb.href?.startsWith('/jobs/')
    )?.href;
    if (jobHref) {
      navigate(`${jobHref}/candidates`);
    }
  }, [navigate, currentInterviewReport, currentSkillAssessmentReport]);

  const handleActionClick = useCallback((action: 'hold' | 'reject' | 'advance') => {
    setActionModal({ open: true, action });
  }, []);

  const handleActionConfirm = useCallback(
    async (notes: string) => {
      if (!candidateId || !actionModal.action) return;

      try {
        await dispatch(
          candidateAction({
            candidateId,
            action: actionModal.action,
            notes
          })
        ).unwrap();

        setActionModal({ open: false, action: null });

        // Navigate back to candidates page after successful action
        if (currentInterviewReport?.job.job_id) {
          navigate(`/jobs/${currentInterviewReport.job.job_id}/candidates`);
        } else {
          handleBackToCandidates();
        }
      } catch (error) {
        console.error(`Failed to ${actionModal.action} candidate:`, error);
      }
    },
    [dispatch, candidateId, actionModal.action, navigate, currentInterviewReport, handleBackToCandidates]
  );

  const handleActionModalClose = useCallback(() => {
    setActionModal({ open: false, action: null });
  }, []);

  const handleDownloadReport = useCallback(async () => {
    if (downloading) return;

    setDownloading(true);
    try {
      if (activeTab === 'interview' && currentInterviewReport) {
        generateInterviewReportPDF(currentInterviewReport);
      } else if (activeTab === 'skillAssessment' && currentSkillAssessmentReport) {
        generateSkillAssessmentReportPDF(currentSkillAssessmentReport);
      } else {
        throw new Error('No report data available');
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [downloading, currentInterviewReport, currentSkillAssessmentReport, activeTab]);

  const handleJumpToTimestamp = useCallback((seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = seconds;
      videoRef.current.play();
      setVideoPlaying(true);
    }
  }, []);

  const toggleVideoPlay = useCallback(() => {
    if (videoRef.current) {
      if (videoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setVideoPlaying(!videoPlaying);
    }
  }, [videoPlaying]);

  const handleVideoTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const getInitials = useCallback((name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, []);

  const getSeverityIcon = useCallback((severity: string) => {
    switch (severity) {
      case 'positive':
        return <CheckCircleIcon className={styles.evidenceIconPositive} />;
      case 'warning':
        return <WarningIcon className={styles.evidenceIconWarning} />;
      case 'negative':
        return <ErrorIcon className={styles.evidenceIconNegative} />;
      default:
        return null;
    }
  }, []);

  const capitalizeFirstLetter = useCallback((text: string): string => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }, []);

  const videoPlaybackUrl = useMemo(() => {
    return currentInterviewReport?.video?.playback_url || currentInterviewReport?.recording?.url || '';
  }, [currentInterviewReport?.recording?.url, currentInterviewReport?.video?.playback_url]);

  const videoDurationSeconds = useMemo(() => {
    return currentInterviewReport?.video?.duration_seconds || currentInterviewReport?.recording?.duration_seconds || 0;
  }, [currentInterviewReport?.recording?.duration_seconds, currentInterviewReport?.video?.duration_seconds]);

  const hasVideoRecording = useMemo(() => {
    return Boolean(videoPlaybackUrl.trim());
  }, [videoPlaybackUrl]);

  const interviewUnavailable = !reportLoading && (Boolean(reportError) || !currentInterviewReport);
  const skillAssessmentUnavailable =
    !skillAssessmentReportLoading && (Boolean(skillAssessmentReportError) || !currentSkillAssessmentReport);
  const hasInterviewReport = Boolean(currentInterviewReport);
  const hasSkillAssessmentReport = Boolean(currentSkillAssessmentReport);
  const loadingAnyReport =
    (reportLoading && !currentInterviewReport) || (skillAssessmentReportLoading && !currentSkillAssessmentReport);

  // Show loading state only if BOTH reports are loading and neither has loaded yet
  if (loadingAnyReport && !hasInterviewReport && !hasSkillAssessmentReport) {
    return <PageLoader label="Loading report..." activeNav="home" showSidebar={false} />;
  }

  // Show error only if BOTH reports failed (neither loaded successfully)
  const bothReportsUnavailable = interviewUnavailable && skillAssessmentUnavailable;
  if (!hasInterviewReport && !hasSkillAssessmentReport && bothReportsUnavailable) {
    return (
      <div className={styles.container}>
        <Sidebar activeNav="home" user={user || undefined} onLogout={handleLogout} />
        <main className={styles.mainContent}>
          <div className={styles.errorContainer}>
            <h2>Failed to load report</h2>
            <p>{reportError || skillAssessmentReportError || 'An unexpected error occurred.'}</p>
            <button
              onClick={() => {
                if (candidateId) {
                  dispatch(fetchInterviewReport({ candidateId }));
                  dispatch(fetchSkillAssessmentReport({ candidateId }));
                }
              }}
            >
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  const candidate = currentInterviewReport?.candidate || {
    candidate_id: currentSkillAssessmentReport?.header.candidate_id || '',
    name: currentSkillAssessmentReport?.header.candidate_name || 'Candidate',
    email: ''
  };
  const job = currentInterviewReport?.job || {
    job_id:
      currentSkillAssessmentReport?.header.breadcrumbs
        .find((crumb) => crumb.href?.startsWith('/jobs/'))
        ?.href?.split('/')[2] || '',
    title: currentSkillAssessmentReport?.header.role_title || 'Role'
  };
  const overall_score = currentInterviewReport?.overall_score ?? currentSkillAssessmentReport?.overall.score ?? 0;
  const recommendation_reason =
    currentInterviewReport?.recommendation_reason || currentSkillAssessmentReport?.overall.summary || '';
  const skillProfile = currentSkillAssessmentReport?.profile;

  // Check which interview channels were configured
  const deliveryChannels = candidate.interview_configuration?.delivery_channels || [];
  const interviewHostConfigured = deliveryChannels.includes('interview_host');
  const skillAssessmentInChannels = deliveryChannels.includes('ai_skill_assessment');

  // Check skill assessment state
  const skillAssessmentState = candidate.skill_assessment?.state || 'not_started';
  const skillAssessmentConfigured = skillAssessmentState !== 'not_started' || skillAssessmentInChannels;
  const skillAssessmentCompleted = skillAssessmentState === 'completed';

  // Show tabs only for configured channels
  const showInterviewTab = interviewHostConfigured || hasInterviewReport; // Show if configured OR has data
  const showSkillAssessmentTab = skillAssessmentConfigured; // Show if configured

  // Enable action buttons if EITHER interview OR skill assessment is completed
  // Recruiters can make decisions based on either assessment type
  const decisionActionsDisabled = !hasInterviewReport && !hasSkillAssessmentReport; // Enable if ANY report is available

  return (
    <div className={styles.container}>
      <Sidebar activeNav="home" user={user || undefined} onLogout={handleLogout} />
      <main className={styles.mainContent}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumb}>
          <button className={styles.breadcrumbLink} onClick={handleBackToHome}>
            Home
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <button className={styles.breadcrumbLink} onClick={handleBackToJobs}>
            Jobs
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <button className={styles.breadcrumbLink} onClick={handleBackToCandidates}>
            {job.title}
          </button>
          <ChevronRightIcon className={styles.breadcrumbSeparator} />
          <span className={styles.breadcrumbCurrent}>{candidate.name} - Report</span>
        </div>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.avatar}>{getInitials(candidate.name)}</div>
            <div className={styles.headerInfo}>
              <h1 className={styles.candidateName}>{candidate.name}</h1>
              <p className={styles.candidateMeta}>{job.title}</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.actionBar}>
            <button className={styles.btnSecondary} onClick={handleDownloadReport} disabled={downloading}>
              <FileDownloadIcon sx={{ fontSize: 18 }} />
              {downloading ? 'Downloading...' : 'Download'}
            </button>
            <button
              className={styles.btnHold}
              onClick={() => handleActionClick('hold')}
              disabled={decisionActionsDisabled}
              title={decisionActionsDisabled ? 'Available once a report is ready' : undefined}
            >
              <PauseIcon sx={{ fontSize: 18 }} />
              Hold
            </button>
            <button
              className={styles.btnReject}
              onClick={() => handleActionClick('reject')}
              disabled={decisionActionsDisabled}
              title={decisionActionsDisabled ? 'Available once a report is ready' : undefined}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
              Reject
            </button>
            <button
              className={styles.btnAdvance}
              onClick={() => handleActionClick('advance')}
              disabled={decisionActionsDisabled}
              title={decisionActionsDisabled ? 'Available once a report is ready' : undefined}
            >
              Advance to Next round
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        {/* Report Tabs */}
        <div className={styles.reportTabs}>
          {showInterviewTab && (
            <button
              type="button"
              className={`${styles.reportTab} ${activeTab === 'interview' ? styles.reportTabActive : ''} ${
                !hasInterviewReport ? styles.reportTabDisabled : ''
              }`}
              onClick={() => hasInterviewReport && setActiveTab('interview')}
              disabled={!hasInterviewReport}
              title={!hasInterviewReport ? 'Interview report is not available for this candidate' : undefined}
            >
              <MicIcon sx={{ fontSize: 18 }} />
              Interview Report
              {hasInterviewReport && <span className={styles.reportTabScoreBadge}>{overall_score}</span>}
            </button>
          )}
          {showSkillAssessmentTab && (
            <button
              type="button"
              className={`${styles.reportTab} ${activeTab === 'skillAssessment' ? styles.reportTabActive : ''}`}
              onClick={() => setActiveTab('skillAssessment')}
            >
              <AssignmentIcon sx={{ fontSize: 18 }} />
              Skill Assessment
              {hasSkillAssessmentReport &&
                currentSkillAssessmentReport &&
                currentSkillAssessmentReport.overall.score !== null && (
                  <span className={styles.reportTabScoreBadge}>{currentSkillAssessmentReport.overall.score}</span>
                )}
            </button>
          )}
        </div>

        {activeTab === 'interview' && hasInterviewReport && currentInterviewReport && (
          <>
            {/* Score and Recommendation */}
            <div className={styles.topSection}>
              <div className={styles.scoreCircle}>
                <svg className={styles.circularProgress} viewBox="0 0 120 120">
                  <circle className={styles.circleBackground} cx="60" cy="60" r="54" />
                  <circle
                    className={styles.circleProgress}
                    cx="60"
                    cy="60"
                    r="54"
                    style={{
                      strokeDasharray: `${(overall_score / 100) * 339.292} 339.292`,
                      stroke: getScoreColor(overall_score).primary
                    }}
                  />
                </svg>
                <div className={styles.scoreText}>
                  <span className={styles.scoreNumber} style={{ color: getScoreColor(overall_score).primary }}>
                    {overall_score}
                  </span>
                  <span className={styles.scoreLabel}>OVERALL</span>
                </div>
              </div>

              <div className={styles.recommendationCard}>
                <div
                  className={styles.recommendationBadge}
                  style={{
                    background: getRecommendationColor(currentInterviewReport.recommendation).bg,
                    color: getRecommendationColor(currentInterviewReport.recommendation).text,
                    border: `1px solid ${getRecommendationColor(currentInterviewReport.recommendation).border}`
                  }}
                >
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                  Recommendation: {currentInterviewReport.recommendation}
                </div>
                <p className={styles.recommendationText}>{recommendation_reason}</p>
              </div>
            </div>

            {/* Main Grid */}
            <div className={styles.mainGrid}>
              {/* Left Column */}
              <div className={styles.leftColumn}>
                {/* Video Player */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>🎥</span>
                    Interview recording
                  </h3>
                  <div className={styles.videoContainer}>
                    {hasVideoRecording ? (
                      <>
                        <div className={styles.videoWrapper}>
                          <video
                            ref={videoRef}
                            className={styles.video}
                            onTimeUpdate={handleVideoTimeUpdate}
                            src={videoPlaybackUrl}
                          />
                          <button className={styles.videoPlayButton} onClick={toggleVideoPlay}>
                            {videoPlaying ? (
                              <PauseIcon sx={{ fontSize: 48 }} />
                            ) : (
                              <PlayArrowIcon sx={{ fontSize: 48 }} />
                            )}
                          </button>
                          <div className={styles.proctorBadge}>
                            <span className={styles.proctorIcon}>👁️</span>
                            Proctored session
                          </div>
                        </div>
                        <div className={styles.videoControls}>
                          <span className={styles.videoTime}>{formatTime(currentTime)}</span>
                          <input
                            type="range"
                            className={styles.videoSeeker}
                            min="0"
                            max={videoDurationSeconds}
                            value={currentTime}
                            onChange={(e) => {
                              const time = Number(e.target.value);
                              setCurrentTime(time);
                              if (videoRef.current) {
                                videoRef.current.currentTime = time;
                              }
                            }}
                          />
                          <span className={styles.videoTime}>{formatTime(videoDurationSeconds)}</span>
                        </div>
                        <p className={styles.videoHint}>Click any evidence timestamp to jump to that answer</p>
                      </>
                    ) : (
                      <div className={styles.videoPlaceholder}>
                        <div className={styles.placeholderIcon}>
                          <svg
                            width="64"
                            height="64"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                          >
                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h4 className={styles.placeholderTitle}>Video not available at the moment</h4>
                      </div>
                    )}
                  </div>
                </div>

                {/* Evidence */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>💡</span>
                    Evidence-backed reasoning
                  </h3>
                  <div className={styles.evidenceList}>
                    {currentInterviewReport.evidence.map((item, index) => (
                      <div
                        key={index}
                        className={`${styles.evidenceItem} ${
                          styles[`evidence${item.severity.charAt(0).toUpperCase() + item.severity.slice(1)}`]
                        }`}
                      >
                        {getSeverityIcon(item.severity)}
                        <div className={styles.evidenceContent}>
                          <h4 className={styles.evidenceClaim}>{capitalizeFirstLetter(item.claim)}</h4>
                          {hasVideoRecording && item.timestamp_label && (
                            <button
                              className={styles.evidenceTimestamp}
                              onClick={() => handleJumpToTimestamp(item.timestamp_seconds)}
                            >
                              <PlayArrowIcon sx={{ fontSize: 14 }} />
                              Answer @{item.timestamp_label}
                            </button>
                          )}
                          <p className={styles.evidenceExcerpt}>"{item.excerpt}"</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className={styles.rightColumn}>
                {/* Competency Scores */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>📊</span>
                    Competency scores
                  </h3>
                  <div className={styles.competencyList}>
                    {currentInterviewReport.competency_scores.map((comp, index) => (
                      <div key={index} className={styles.competencyItem}>
                        <div className={styles.competencyHeader}>
                          <span className={styles.competencyName}>{capitalizeFirstLetter(comp.competency)}</span>
                          <span className={styles.competencyScore} style={{ color: getScoreColor(comp.score).primary }}>
                            {comp.score}
                          </span>
                        </div>
                        <div className={styles.competencyBar}>
                          <div
                            className={styles.competencyFill}
                            style={{
                              width: `${(comp.score / comp.max_score) * 100}%`,
                              background: `linear-gradient(90deg, ${getScoreColor(comp.score).light} 0%, ${getScoreColor(comp.score).dark} 100%)`
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Integrity & Verification */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <span className={styles.sectionIcon}>🔒</span>
                    Integrity & verification
                  </h3>
                  <div className={styles.integrityList}>
                    <div className={styles.integrityItem}>
                      <span className={styles.integrityLabel}>
                        <span className={styles.integrityIcon}>📧</span>
                        Email OTP verified
                      </span>
                      <span className={styles.integrityValue}>
                        {currentInterviewReport.integrity.otp_verified ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className={styles.integrityItem}>
                      <span className={styles.integrityLabel}>
                        <span className={styles.integrityIcon}>👤</span>
                        Single person on camera
                      </span>
                      <span className={styles.integrityValue}>
                        {/* {interviewReport.integrity.single_person_confirmed ? 'Confirmed' : 'Multiple'} */}
                        N/A
                      </span>
                    </div>
                    <div className={styles.integrityItem}>
                      <span className={styles.integrityLabel}>
                        <span className={styles.integrityIcon}>🤖</span>
                        AI-response likelihood
                      </span>
                      <span className={styles.integrityValue}>
                        {interviewReport.integrity.ai_response_likelihood || 'N/A'}
                      </span>
                    </div>
                    <div className={styles.integrityItem}>
                      <span className={styles.integrityLabel}>
                        <span className={styles.integrityIcon}>💻</span>
                        Multi-device attempt
                      </span>
                      <span className={styles.integrityValue}>
                        {interviewReport.integrity.multi_device_attempt ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className={styles.integrityItem}>
                      <span className={styles.integrityLabel}>
                        <span className={styles.integrityIcon}>⚠️</span>
                        Focus loss (tab switch)
                      </span>
                      <span className={styles.integrityValue}>{interviewReport.integrity.tab_switches || 'N/A'}</span>
                    </div>
                    <div className={styles.integrityItem}>
                      <span className={styles.integrityLabel}>
                        <span className={styles.integrityIcon}>🗣️</span>
                        Speaking-speed anomaly
                      </span>
                      <span className={styles.integrityValue}>
                        {interviewReport.integrity.speaking_speed_anomaly === true
                          ? 'Yes'
                          : interviewReport.integrity.speaking_speed_anomaly === false
                            ? 'No'
                            : 'N/A'}
                      </span>
                    </div>
                    <div className={styles.integrityItem}>
                      <span className={styles.integrityLabel}>
                        <span className={styles.integrityIcon}>⏸️</span>
                        Suspicious pause patterns
                      </span>
                      <span className={styles.integrityValue}>
                        {interviewReport.integrity.suspicious_pause_patterns === true
                          ? 'Yes'
                          : interviewReport.integrity.suspicious_pause_patterns === false
                            ? 'No'
                            : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Transcript */}
            <div className={styles.section}>
              <div className={styles.transcriptHeader}>
                <h3 className={styles.sectionTitle}>
                  <span className={styles.sectionIcon}>📄</span>
                  Transcript
                </h3>
                <button className={styles.expandButton} onClick={() => setTranscriptExpanded(!transcriptExpanded)}>
                  {transcriptExpanded ? (
                    <>
                      Collapse
                      <ExpandLessIcon sx={{ fontSize: 20 }} />
                    </>
                  ) : (
                    <>
                      Expand
                      <ExpandMoreIcon sx={{ fontSize: 20 }} />
                    </>
                  )}
                </button>
              </div>
              <div className={`${styles.transcriptList} ${transcriptExpanded ? styles.transcriptExpanded : ''}`}>
                {currentInterviewReport.transcript.map((item, index) => (
                  <div
                    key={index}
                    className={`${styles.transcriptItem} ${
                      item.speaker === 'interviewer' ? styles.transcriptInterviewer : styles.transcriptCandidate
                    }`}
                  >
                    <div className={styles.transcriptSpeaker}>
                      <span
                        className={`${styles.speakerLabel} ${
                          item.speaker === 'interviewer' ? styles.speakerAgent : styles.speakerCandidate
                        }`}
                      >
                        {item.speaker_label}
                      </span>
                      <span className={styles.transcriptTime}>{item.timestamp_label}</span>
                    </div>
                    <p className={styles.transcriptText}>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'skillAssessment' && hasSkillAssessmentReport && currentSkillAssessmentReport && (
          <>
            {/* Skill Assessment Score and Recommendation */}
            <div className={styles.topSection}>
              <div className={styles.scoreCircle}>
                <svg className={styles.circularProgress} viewBox="0 0 120 120">
                  <circle className={styles.circleBackground} cx="60" cy="60" r="54" />
                  <circle
                    className={styles.circleProgress}
                    cx="60"
                    cy="60"
                    r="54"
                    style={{
                      strokeDasharray: `${(currentSkillAssessmentReport.overall.score / 100) * 339.292} 339.292`,
                      stroke: getScoreColor(currentSkillAssessmentReport.overall.score).primary
                    }}
                  />
                </svg>
                <div className={styles.scoreText}>
                  <span
                    className={styles.scoreNumber}
                    style={{ color: getScoreColor(currentSkillAssessmentReport.overall.score).primary }}
                  >
                    {currentSkillAssessmentReport.overall.score}
                  </span>
                  <span className={styles.scoreLabel}>OVERALL</span>
                </div>
              </div>

              <div className={styles.recommendationCard}>
                <div
                  className={styles.recommendationBadge}
                  style={{
                    background: getRecommendationColor(currentSkillAssessmentReport.overall.recommendation).bg,
                    color: getRecommendationColor(currentSkillAssessmentReport.overall.recommendation).text
                  }}
                >
                  {currentSkillAssessmentReport.overall.recommendation?.toUpperCase() || 'PENDING'}
                </div>
                <p className={styles.recommendationText}>
                  {currentSkillAssessmentReport.overall.summary || 'Assessment in progress...'}
                </p>
              </div>
            </div>

            {/* Skill Area Scores */}
            {currentSkillAssessmentReport.skill_area_scores &&
              currentSkillAssessmentReport.skill_area_scores.length > 0 && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Skill Areas</h3>
                  <div className={styles.skillAreasGrid}>
                    {currentSkillAssessmentReport.skill_area_scores.map((area, index) => (
                      <div key={index} className={styles.skillAreaCard}>
                        <h4 className={styles.skillAreaName}>{area.skill}</h4>
                        <div className={styles.skillAreaScore}>
                          <span className={styles.scoreValue}>
                            {area.score}/{area.max_score}
                          </span>
                          <span className={`${styles.levelBadge} ${styles[area.level]}`}>{area.level}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* AI Proficiency Level Card */}
            {currentSkillAssessmentReport.profile &&
              (currentSkillAssessmentReport.profile.level_label ||
                currentSkillAssessmentReport.profile.star_rating) && (
                <div className={styles.section}>
                  <div className={styles.profileCard}>
                    <div className={styles.profileCardContent}>
                      <div className={styles.profileLevel}>
                        <span className={styles.profileLevelLabel}>Assessed Level:</span>
                        <span className={styles.profileLevelValue}>
                          {currentSkillAssessmentReport.profile.level_label || 'Not Available'}
                        </span>
                      </div>
                      {currentSkillAssessmentReport.profile.star_rating > 0 && (
                        <div className={styles.profileStars}>
                          {'⭐'.repeat(currentSkillAssessmentReport.profile.star_rating)}
                          <span className={styles.profileStarsText}>
                            {' '}
                            ({currentSkillAssessmentReport.profile.star_rating}/5 stars)
                          </span>
                        </div>
                      )}
                    </div>
                    <p className={styles.profileNote}>
                      ℹ️ This assessment adapts to your answers. The level shown represents what you demonstrated, not
                      what you initially selected.
                    </p>
                  </div>
                </div>
              )}

            {/* Profile Summary - Key Strengths & Learning Opportunities */}
            {currentSkillAssessmentReport.profile && (
              <>
                {/* Key Strengths */}
                {currentSkillAssessmentReport.profile.key_strengths?.bullets &&
                  currentSkillAssessmentReport.profile.key_strengths.bullets.length > 0 && (
                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <CheckCircleIcon sx={{ color: '#5fcfac', fontSize: 20, marginRight: 1 }} />
                        {currentSkillAssessmentReport.profile.key_strengths?.title || 'Strengths'}
                      </h3>
                      {currentSkillAssessmentReport.profile.key_strengths?.summary && (
                        <p className={styles.profileSummary}>
                          {currentSkillAssessmentReport.profile.key_strengths.summary}
                        </p>
                      )}
                      <ul className={styles.bulletList}>
                        {currentSkillAssessmentReport.profile.key_strengths.bullets.map((item: any, index: number) => (
                          <li key={index} className={styles.bulletItem}>
                            <strong className={styles.bulletTitle}>{item.title}</strong>
                            {item.detail && <span className={styles.bulletDetail}> - {item.detail}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                {/* Learning Opportunities */}
                {currentSkillAssessmentReport.profile.learning_opportunities?.bullets &&
                  currentSkillAssessmentReport.profile.learning_opportunities.bullets.length > 0 && (
                    <div className={styles.section}>
                      <h3 className={styles.sectionTitle}>
                        <WarningIcon sx={{ color: '#ff9800', fontSize: 20, marginRight: 1 }} />
                        {currentSkillAssessmentReport.profile.learning_opportunities?.title || 'Learning Opportunities'}
                      </h3>
                      {currentSkillAssessmentReport.profile.learning_opportunities?.summary && (
                        <p className={styles.profileSummary}>
                          {currentSkillAssessmentReport.profile.learning_opportunities.summary}
                        </p>
                      )}
                      <ul className={styles.bulletList}>
                        {currentSkillAssessmentReport.profile.learning_opportunities.bullets.map(
                          (item: any, index: number) => (
                            <li key={index} className={styles.bulletItem}>
                              <strong className={styles.bulletTitle}>{item.title}</strong>
                              {item.detail && <span className={styles.bulletDetail}> - {item.detail}</span>}
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
              </>
            )}
          </>
        )}
      </main>

      {/* Action Modal */}
      {actionModal.open && actionModal.action && (
        <ActionModal
          open={actionModal.open}
          onClose={handleActionModalClose}
          onConfirm={handleActionConfirm}
          action={actionModal.action}
          candidateName={candidate.name}
        />
      )}
    </div>
  );
};

export default InterviewReport;
